import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MobileLinkTree } from "@/components/MobileLinkTree";
import { DesktopHome } from "@/components/DesktopHome";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import NossaHistoria from "./pages/NossaHistoria";
import Servicos from "./pages/Servicos";
import Blog from "./pages/Blog";
import Imprensa from "./pages/Imprensa";
import Contato from "./pages/Contato";
import Consultorios from "./pages/Consultorios";
import AvaliacaoCaso from "./pages/AvaliacaoCaso";
import Sitemap from "./pages/Sitemap";
import AreaPaciente from "./pages/AreaPaciente";
import MeusDados from "./pages/MeusDados";
import Preferencias from "./pages/Preferencias";
import Loja from "./pages/Loja";
import EmConstrucao from "./pages/EmConstrucao";
import ResetarSenha from "./pages/ResetarSenha";
import { AuthProvider } from "@/hooks/useAuth";
import { useSiteMode } from "@/hooks/useSiteMode";
import { useSiteLogo } from "@/hooks/useSiteLogo";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { SeoManager } from "@/components/SeoManager";

// Create QueryClient outside component to prevent recreation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior }); }, [pathname]);
  return null;
}

function FaviconUpdater() {
  const logoUrl = useSiteLogo();
  useEffect(() => {
    if (!logoUrl) return;
    const selectors = ['link[rel="icon"]', 'link[rel="alternate icon"]', 'link[rel="apple-touch-icon"]'];
    selectors.forEach(sel => {
      const el = document.querySelector<HTMLLinkElement>(sel);
      if (el) el.href = logoUrl;
    });
  }, [logoUrl]);
  return null;
}

function FloatingComponents() {
  const location = useLocation();
  const isAdminPanel = location.pathname === "/logins" && 
    new URLSearchParams(location.search).get('admin') === 'true';
  
  if (isAdminPanel) {
    return null;
  }
  
  return (
    <>
      <FloatingWhatsApp />
    </>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f4fbf7] to-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-brand-primary font-medium">Carregando...</p>
      </div>
    </div>
  );
}

function MobileHomeRoute() {
  const { getSetting } = useSiteSettings();
  const mobileHomeLink = getSetting("mobile_header_home", "linktree");
  if (window.innerWidth < 768) {
    return mobileHomeLink === "inicio"
      ? <Navigate to="/inicio" replace />
      : <MobileLinkTree />;
  }
  return <Navigate to="/inicio" replace />;
}

function AppRoutes() {
  const { mode, isLoading } = useSiteMode();

  if (isLoading) {
    return <LoadingScreen />;
  }

  const isConstructionMode = mode === 'construction';

  return (
    <>
      {isConstructionMode ? (
        // Construction mode - show holding page
        <Routes>
          <Route path="/logins" element={<AreaPaciente />} />
          <Route path="*" element={<EmConstrucao />} />
        </Routes>
      ) : (
        // Production mode - show full site
        <>
          <Routes>
            {/* "/" → linktree no mobile (configurável), home normal no desktop */}
            <Route path="/" element={<MobileHomeRoute />} />
            <Route path="/inicio" element={<Index />} />
            <Route path="/sobre" element={<NossaHistoria />} />
            <Route path="/servicos" element={<Servicos />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<Blog />} />
            <Route path="/imprensa" element={<Imprensa />} />
            <Route path="/contato" element={<Contato />} />
            <Route path="/consultorios" element={<Consultorios />} />
            <Route path="/avaliacoes" element={<AvaliacaoCaso />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/logins" element={<AreaPaciente />} />
            <Route path="/meus-dados" element={<Navigate to="/inicio" replace />} />
            <Route path="/preferencias" element={<Navigate to="/inicio" replace />} />
            <Route path="/loja" element={<Loja />} />
            <Route path="/resetar-senha" element={<ResetarSenha />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <FloatingComponents />
        </>
      )}
    </>
  );
}

function AppContent() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <FaviconUpdater />
        <SeoManager />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppContent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
